import { useEffect } from "react";

/**
 * Tells iPhone and iPad Safari that the page is in a call, so the call is
 * heard with the ring/silent switch on silent.
 *
 * The call plays through Web Audio (`webAudioMix`), and iOS plays Web Audio
 * in the "ambient" category until the page captures the microphone: on
 * silent, the call is mute. Unmuting starts capture, iOS moves the page to
 * "play-and-record" and the call becomes audible; that was the "I have to
 * unmute to hear" report (2026-11-03), and it hit everyone who joins muted,
 * including every accepted incoming call. Asking for "play-and-record" from
 * the join on puts the page in that same state from the start.
 *
 * `navigator.audioSession` is the W3C Audio Session API (Safari 16.4+).
 * Touch devices only: a Mac has no silent switch, and a desktop browser's
 * routing is left as it was.
 */

type AudioSessionType =
  | "auto"
  | "playback"
  | "transient"
  | "transient-solo"
  | "ambient"
  | "play-and-record";

interface AudioSessionLike {
  type: AudioSessionType;
}

function touchAudioSession(): AudioSessionLike | null {
  if (typeof navigator === "undefined" || !(navigator.maxTouchPoints > 0)) return null;
  const session = (navigator as Navigator & { audioSession?: AudioSessionLike }).audioSession;
  return session && typeof session === "object" ? session : null;
}

/** Call from the Join tap (before any await) and while a call is active. */
export function enterCallAudioSession(): void {
  const session = touchAudioSession();
  if (!session) return;
  try {
    session.type = "play-and-record";
  } catch {
    // An unsupported value throws on some versions; the call still works,
    // only without the silent-switch fix.
  }
}

/** Hand the page back to normal media routing once the call is over. */
export function leaveCallAudioSession(): void {
  const session = touchAudioSession();
  if (!session || session.type !== "play-and-record") return;
  try {
    session.type = "auto";
  } catch {
    // Nothing to undo if the browser refuses.
  }
}

/** Holds the call audio session for as long as `inCall` is true. */
export function useCallAudioSession(inCall: boolean): void {
  useEffect(() => {
    if (!inCall) return;
    enterCallAudioSession();
    return leaveCallAudioSession;
  }, [inCall]);
}
